import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import CrudSection from "@/components/CrudSection";
import { ScheduleBadge, fmtDate } from "@/components/common";
import TruckingMap from "@/components/TruckingMap";
import TruckingDateShift from "@/components/TruckingDateShift";
import DriverAlert from "@/components/DriverAlert";
import NoteCell from "@/components/NoteCell";
import TruckingRequests, { truckingWindowLabel } from "@/components/TruckingRequests";
import { deleteResource } from "@/lib/api";

const SCHEDULERS = ["Bryan", "Boone", "Graves", "Jeff", "Shop", "Parts", "Matt"];
const today = new Date().toISOString().slice(0, 10);

const fields = [
  { key: "entered_date", label: "Date Entered", type: "date", default: today, required: true },
  { key: "date", label: "Scheduled Date", type: "date" },
  { key: "request_range", label: "OR Schedule Timeframe", type: "daterange", startKey: "request_start", endKey: "request_end", full: true },
  { key: "customer", label: "Customer", type: "text", required: true, placeholder: "Clarinda Farm Supply" },
  { key: "customer_phone", label: "Customer Phone", type: "tel", required: true, placeholder: "712-555-0123" },
  { key: "address", label: "Location / Address (shown on map)", type: "text", full: true, required: true, placeholder: "112 S 16th St, Clarinda, IA" },
  { key: "pickup_unit", label: "Pickup Unit (from customer)", type: "text", full: true, placeholder: "Trade-in / unit to pick up" },
  { key: "pickup_na", label: "No pickup", type: "checkbox", checkboxLabel: "N/A — nothing to pick up" },
  { key: "deliver_unit", label: "Deliver Unit (to customer)", type: "text", full: true, placeholder: "Unit to deliver" },
  { key: "deliver_na", label: "No delivery", type: "checkbox", checkboxLabel: "N/A — nothing to deliver" },
  { key: "scheduled_by", label: "Scheduled By", type: "select", default: "Bryan", required: true, options: SCHEDULERS },
  { key: "initials", label: "Your Initials", type: "text", required: true, maxLength: 2, placeholder: "e.g. BW" },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];

const unitCell = (val, na) => na ? <span className="text-xs font-bold uppercase tracking-wide text-gray-400">N/A</span> : (val || <span className="text-gray-300">—</span>);

// "Done" removes the stop completely (with a quick confirm so an accidental tap can't wipe it).
const CompleteButton = ({ row }) => {
  const qc = useQueryClient();
  const [busy, setBusy] = React.useState(false);
  const done = async () => {
    setBusy(true);
    try {
      await deleteResource("trucking", row.id);
      qc.invalidateQueries({ queryKey: ["trucking"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success("Stop completed & removed");
    } catch {
      toast.error("Couldn't complete");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="trucking-complete-btn"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-[#00913C] text-[#00913C] text-xs font-bold uppercase tracking-wide hover:bg-[#00913C] hover:text-white transition-colors">
          <CheckCircle2 className="w-3.5 h-3.5" /> Done
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-none border-2 border-gray-900">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-head uppercase">Mark {row.customer || "this stop"} done?</AlertDialogTitle>
          <AlertDialogDescription>This removes the stop from Trucking completely. It can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-2">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="trucking-complete-confirm" onClick={done} disabled={busy}
            className="rounded-none bg-[#00913C] hover:bg-[#00753a]">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Done — Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const scheduleCell = (r) => {
  if (r.date) return <ScheduleBadge date={r.date} />;
  if (r.request_start) return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className="inline-block text-xs font-bold uppercase tracking-wide px-2 py-1 bg-[#E0A81E] text-[#16265F]">Requested</span>
      <span className="text-xs text-gray-600">{truckingWindowLabel(r)}</span>
    </span>
  );
  return <span className="text-gray-300">—</span>;
};

const columns = [
  { key: "entered_date", label: "Entered", mono: true, render: (r) => r.entered_date ? fmtDate(r.entered_date) : <span className="text-gray-300">—</span> },
  { key: "customer", label: "Customer" },
  { key: "customer_phone", label: "Phone", render: (r) => r.customer_phone ? <a href={`tel:${r.customer_phone}`} className="text-[#2962FF] underline">{r.customer_phone}</a> : <span className="text-gray-300">—</span> },
  { key: "address", label: "Location", render: (r) => r.address || <span className="text-gray-300">—</span> },
  { key: "pickup_unit", label: "Pickup Unit", render: (r) => unitCell(r.pickup_unit, r.pickup_na) },
  { key: "deliver_unit", label: "Deliver Unit", render: (r) => unitCell(r.deliver_unit, r.deliver_na) },
  { key: "scheduled_by", label: "Scheduled By", render: (r) => (
      <div className="flex flex-col">
        <span className="font-medium">{r.scheduled_by || "—"}</span>
        {r.initials && <span className="text-xs text-gray-500 uppercase font-mono-data">{r.initials}</span>}
      </div>
    ) },
  { key: "date", label: "Scheduled", render: scheduleCell },
  { key: "notes", label: "Notes", render: (r) => <NoteCell row={r} path="trucking" /> },
  { key: "complete", label: "Done", render: (r) => <CompleteButton row={r} /> },
];

export default function Trucking() {
  return (
    <CrudSection
      title="Trucking"
      subtitle="Driver's runs — pickups from and deliveries to customers (one stop can do both)"
      path="trucking"
      testPrefix="trucking"
      fields={fields}
      columns={columns}
      filter={(r) => !r.completed}
      noDelete
      validate={(v) => {
        if (!String(v.date || "").trim() && !String(v.request_start || "").trim())
          return "Enter a Scheduled Date OR a Schedule Timeframe (From)";
        if (!v.pickup_na && !String(v.pickup_unit || "").trim() && !v.deliver_na && !String(v.deliver_unit || "").trim())
          return "Enter a Pickup or Deliver unit, or check N/A for each";
        if (!v.pickup_na && !String(v.pickup_unit || "").trim()) return "Enter a Pickup unit or check its N/A box";
        if (!v.deliver_na && !String(v.deliver_unit || "").trim()) return "Enter a Deliver unit or check its N/A box";
        return null;
      }}
      extraHeader={
        <>
          <DriverAlert mode="bar" />
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0"><TruckingRequests /></div>
            <div className="flex items-center gap-2 shrink-0">
              <DriverAlert mode="button" />
              <TruckingDateShift />
            </div>
          </div>
        </>
      }
      extraFooter={<TruckingMap />}
    />
  );
}
